import React, { useState } from "react";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { CircularProgress, IconButton, Tooltip } from "@mui/material";
import { GeneralActions } from "Components/AppGrid/shipeezi-new-grid";
import { colors } from "@zapalacorp/tokens";
import { Filter, SearchNormal as IconSearch } from "iconsax-react";
import { Filters } from "Models/shipeezi.table";
import { useTranslation } from "react-i18next";

import { filters } from "../../../../../../../../translate/locales/en_US";
import { ResourceFilterMenu } from "../ResourceFilterMenu";
import { LoadingMore } from "./LoadingMore";
import {
  AccordionDetails,
  AccordionSummary,
  Badge,
  CountBadge,
  CustomAccordion,
  FilterByOptionsContainer,
  FilterOptionsWrapper,
  InputContainer,
  LoadMoreContainer,
  SearchInput,
} from "./styles";

interface ResourceSectionListSearchProps {
  isActiveFilterByOptions?: boolean;
  handleSearchChange?: (newValue: string) => void;
  handleFilterClick?: (event: React.MouseEvent<HTMLElement>) => void;
  activeFilters?: Filters;
}

export const ResourceSectionListSearch = (props: ResourceSectionListSearchProps) => {
  const {
    isActiveFilterByOptions = false,
    handleSearchChange,
    handleFilterClick,
    activeFilters,
  } = props;
  const { t: translate } = useTranslation();

  const [search, setSearch] = useState("");

  const getTotalOfFilters = () => {
    let count = 0;

    if (activeFilters) {
      const keys = Object.getOwnPropertyNames(activeFilters);

      if (keys && keys?.length > 0) {
        keys.map((key) => {
          if (activeFilters && activeFilters[key] && Array.isArray(activeFilters[key])) {
            const filterArray = activeFilters[key];

            count += filterArray.length;
          }

          return null;
        });
      }
    }

    return count;
  };

  const renderEndIcon = () => {
    const countValue = getTotalOfFilters();

    if (filters && Object.getOwnPropertyNames(filters)?.length > 0 && countValue > 0) {
      return <CountBadge>{getTotalOfFilters()}</CountBadge>;
    }

    return null;
  };

  return (
    <InputContainer filter={isActiveFilterByOptions}>
      <SearchInput
        type="text"
        placeholder={translate("typeHereAndPressEnter")}
        value={search}
        onChange={(event) => {
          setSearch(event.target.value);
          if (event.target.value === "" && handleSearchChange) {
            handleSearchChange(event.target.value);
          }
        }}
        filter={false}
        onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
          if (event.key === "Enter" && handleSearchChange) {
            handleSearchChange(search);
          }
        }}
        rightIcon={
          <Tooltip title={translate("pressToSearch")}>
            <IconButton
              style={{ marginRight: "-8px" }}
              onClick={() => handleSearchChange?.(search)}
            >
              <IconSearch size={18} />
            </IconButton>
          </Tooltip>
        }
      />
      {isActiveFilterByOptions && (
        <FilterOptionsWrapper>
          <FilterByOptionsContainer onClick={handleFilterClick}>
            <Filter size="20" color={colors.gray600} />
          </FilterByOptionsContainer>
          {renderEndIcon()}
        </FilterOptionsWrapper>
      )}
    </InputContainer>
  );
};

interface ResourceSectionListProps extends ResourceSectionListSearchProps {
  title: string;
  itemsQuantity?: number;
  children?: React.ReactNode;
  defaultExpanded?: boolean;
  isLoading?: boolean;
  loadMore?: {
    action?: () => void;
    isLoading: boolean;
  };
  generalActions?: GeneralActions;
  activeFilters?: Filters;
  handleActiveFilters?: (filters: Filters) => void;
}

export const ResourceSectionList = (props: ResourceSectionListProps) => {
  const {
    title,
    children,
    defaultExpanded,
    itemsQuantity,
    loadMore,
    handleSearchChange,
    isLoading,
    isActiveFilterByOptions = false,
    generalActions,
    activeFilters,
    handleActiveFilters,
  } = props;
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const { t: translate } = useTranslation();

  const handleFilterClick = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
  };

  const renderLoadMore = () => {
    if (!loadMore) return null;

    return (
      <LoadMoreContainer
        onClick={loadMore.isLoading || !loadMore.action ? undefined : loadMore.action}
      >
        <span>{loadMore.isLoading ? <LoadingMore /> : translate("loadMore")}...</span>
      </LoadMoreContainer>
    );
  };

  const renderContent = () => {
    return (
      <AccordionDetails>
        <ResourceSectionListSearch
          isActiveFilterByOptions={isActiveFilterByOptions}
          handleFilterClick={handleFilterClick}
          handleSearchChange={handleSearchChange}
          activeFilters={activeFilters}
        />
        {children}
        {renderLoadMore()}
      </AccordionDetails>
    );
  };

  return (
    <>
      <CustomAccordion className="dragablepanel" elevation={0} defaultExpanded={defaultExpanded}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          {title} {!isLoading && <Badge>{itemsQuantity ?? 0}</Badge>}{" "}
          {isLoading && <CircularProgress size={16} />}
        </AccordionSummary>
        {renderContent()}
      </CustomAccordion>

      <ResourceFilterMenu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        handleClose={handleCloseMenu}
        generalActions={generalActions}
        handleActiveFilters={handleActiveFilters}
      />
    </>
  );
};
